import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router';
import type { ModuleKey } from '@six-pockets/shared';
import {
  ChartIcon,
  ChevronDownIcon,
  ClockIcon,
  CloseIcon,
  CupIcon,
  HomeIcon,
  LedgerIcon,
  LogoMark,
  MenuIcon,
  ReceiptIcon,
  SettingsIcon,
  SignOutIcon,
  TableTopIcon,
  TagIcon,
  UsersIcon,
} from '@/components/icons';
import { Button, CountBadge } from '@/components/ui';
import { useAuth } from '@/context/AuthContext';
import { useOpenSalesCount } from '@/context/TablesContext';
import { useDismiss } from '@/hooks/useDismiss';
import { cx } from '@/utils/cx';
import styles from '@/styles/components/Sidebar/style.scss';

interface NavChild {
  to: string;
  label: string;
  /** A sub-page with its own module (Bills → Payments, admin only); hidden from a login without it. */
  module?: ModuleKey;
}

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  module: ModuleKey;
  badge?: number;
  /** Makes the item a button that expands to these links (all under `to`) instead of a link. */
  children?: NavChild[];
}

/**
 * An item with sub-pages (Bills, Ledgers): tapping it expands the links below it. It opens by itself on one of its pages,
 * and while collapsed it carries the active highlight.
 */
function NavParent({ item }: { item: NavItem & { children: NavChild[] } }) {
  const { pathname } = useLocation();
  const inside = pathname === item.to || pathname.startsWith(`${item.to}/`);
  const [open, setOpen] = useState(inside);
  const listId = useId();
  const { can } = useAuth();
  const children = item.children.filter((c) => !c.module || can(c.module));

  useEffect(() => {
    if (inside) setOpen(true);
  }, [inside]);

  return (
    <>
      <button
        type="button"
        className={cx(styles.link, styles.parent, inside && !open && styles.active, open && styles.expanded)}
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={styles.icon}>{item.icon}</span>
        <span className={styles.label}>{item.label}</span>
        <ChevronDownIcon size={16} className={styles.chevron} />
      </button>
      {open && (
        <div id={listId} className={styles.children}>
          {children.map((c) => (
            <NavLink key={c.to} to={c.to} end className={({ isActive }) => cx(styles.link, styles.child, isActive && styles.active)}>
              <span className={styles.label}>{c.label}</span>
            </NavLink>
          ))}
        </div>
      )}
    </>
  );
}

function NavGroup({ title, items }: { title: string; items: NavItem[] }) {
  const { can } = useAuth();
  const visible = items.filter((item) => can(item.module));
  if (visible.length === 0) return null;

  return (
    <div className={styles.group}>
      <span className={styles.groupTitle}>{title}</span>
      {visible.map((item) =>
        item.children ? (
          <NavParent key={item.to} item={{ ...item, children: item.children }} />
        ) : (
          <NavLink key={item.to} to={item.to} end={item.to === '/'} className={({ isActive }) => cx(styles.link, isActive && styles.active)}>
            <span className={styles.icon}>{item.icon}</span>
            <span className={styles.label}>{item.label}</span>
            {item.badge !== undefined && <CountBadge count={item.badge} />}
          </NavLink>
        ),
      )}
    </div>
  );
}

/**
 * Side navigation on wide screens. Below the md breakpoint it becomes a top bar (logo + menu button) and the
 * links open as a dropdown, which closes on navigation, Escape, or a tap outside.
 */
export function Sidebar() {
  const openSalesCount = useOpenSalesCount();
  const { user, signOut } = useAuth();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);

  const closeMenu = useCallback(() => setMenuOpen(false), []);
  useDismiss(menuOpen, closeMenu, navRef);
  useEffect(closeMenu, [pathname, closeMenu]);

  return (
    <nav ref={navRef} aria-label="Main" className={cx(styles.sidebar, menuOpen && styles.open)}>
      <div className={styles.topRow}>
        <div className={styles.brand}>
          <LogoMark />
          <div className={styles.brandText}>
            <span className={styles.brandName}>Six Pockets</span>
            {user && <span className={styles.brandSub}>{user.displayName}</span>}
          </div>
        </div>
        <Button
          variant="ghost"
          iconOnly
          size="lg"
          className={styles.menuButton}
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
          aria-controls="main-nav-links"
          icon={menuOpen ? <CloseIcon size={20} /> : <MenuIcon size={22} />}
          onClick={() => setMenuOpen((o) => !o)}
        />
      </div>

      <div id="main-nav-links" className={styles.links}>
        <NavGroup
          title="Operations"
          items={[
            { to: '/', label: 'Tables', icon: <HomeIcon size={20} />, module: 'tables' },
            { to: '/bookings', label: 'Bookings', icon: <ClockIcon size={20} />, module: 'tables' },
            {
              to: '/bills',
              label: 'Bills',
              icon: <ReceiptIcon size={20} />,
              module: 'bills',
              children: [
                { to: '/bills', label: 'Bills home' },
                { to: '/bills/balance', label: 'Balance bills' },
                { to: '/bills/payments', label: 'Payments', module: 'reports' },
              ],
            },
            { to: '/condiments', label: 'Condiments', icon: <CupIcon size={20} />, module: 'condiments', badge: openSalesCount },
            { to: '/customers', label: 'Customers', icon: <UsersIcon size={20} />, module: 'customers' },
            {
              to: '/ledgers',
              label: 'Ledgers',
              icon: <LedgerIcon size={20} />,
              module: 'ledgers',
              children: [
                { to: '/ledgers/cash', label: 'Cash Book' },
                { to: '/ledgers/expenses', label: 'Expense Ledger' },
              ],
            },
          ]}
        />
        <NavGroup
          title="Manage"
          items={[
            { to: '/manage/tables', label: 'Manage tables', icon: <TableTopIcon size={20} />, module: 'settings' },
            { to: '/manage/condiments', label: 'Manage condiments', icon: <TagIcon size={20} />, module: 'settings' },
            { to: '/reports', label: 'Reports', icon: <ChartIcon size={20} />, module: 'reports' },
            {
              to: '/settings',
              label: 'Settings',
              icon: <SettingsIcon size={20} />,
              module: 'settings',
              children: [{ to: '/settings/happy-hour', label: 'Happy hour' }],
            },
          ]}
        />

        {user && (
          <div className={styles.account}>
            <span className={styles.accountName}>{user.displayName}</span>
            <Button size="sm" variant="ghost" icon={<SignOutIcon size={16} />} onClick={signOut}>
              Sign out
            </Button>
          </div>
        )}
      </div>
    </nav>
  );
}
